import {calculatePeriodData} from 'redux/modules/dashboard/selectors'
import {getCurrentTimePeriod} from 'redux/modules/currentUser/selectors'
import {channels} from 'redux/modules/channels/utils'
import {getChannelFullName} from './utils'
import {channelGroups as groups} from '@divvit/common-channels'
import sortBy from 'lodash/sortBy'
import * as _ from 'lodash'

function calculateTrafficData(
  today,
  timePeriod,
  data,
  paid,
  fields,
  unAccumulatedFields,
  timePeriodRanges,
  totals
) {
  const channelsData = Object.keys(data)
    .filter(channel => {
      if (!paid) return true

      const [channelGroupId] = channel.split('/')
      return groups[channelGroupId].paid === true
    })
    .reduce((acc, channel) => {
      acc[channel] = calculatePeriodData(
        today,
        timePeriod,
        data[channel],
        fields,
        unAccumulatedFields,
        timePeriodRanges,
        {
          current: totals.current && totals.current[channel],
          previous: totals.previous && totals.previous[channel]
        }
      )
      return acc
    }, {})

  return Object.keys(channelsData).reduce((acc, channel) => {
    const channelData = channelsData[channel]
    const {values, calculatedValues} = channelData
    const [channelGroupId, channelId] = channel.split('/').map(ch => ch.trim())
    const {label: channelGroupLabel, color: channelGroupColor} = groups[
      channelGroupId
    ] || {label: 'Error group', color: '#ff0000'}

    const {label: channelLabel} = channels[
      `${channelGroupId}/${channelId}`
    ] || {label: 'Error channel'}

    return acc.concat({
      ...{
        channelGroupLabel,
        channelLabel,
        channelGroupColor,
        channelId,
        channelGroupId
      },
      chartData: values,
      ...calculatedValues
    })
  }, [])
}

export function getOrders(state, timePeriodRanges) {
  const {today} = state
  const {
    data,
    sortKey,
    sortDirection,
    totalCurrent: current,
    totalPrevious: previous
  } = state.insights.orders
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !data) return null
  if (!timePeriodRanges.start || !timePeriodRanges.end) return null

  const totals = {current, previous}

  const values = calculateTrafficData(
    today,
    timePeriod,
    data,
    false,
    ['orderCount'],
    [],
    timePeriodRanges,
    totals
  )
  return sortBy(values, value => {
    return sortDirection === 'asc' ? value[sortKey] : -value[sortKey]
  })
}

export function getSelectedMetric(selectedMetric, metrics) {
  if (selectedMetric && metrics.includes(selectedMetric)) return selectedMetric

  return metrics[0]
}

export function getPaidOrders(state, timePeriodRanges, dataKey) {
  const {today} = state
  const {
    data,
    totalCurrent: current,
    totalPrevious: previous
  } = state.insights.orders
  const {sortKey, sortDirection} = getSorting(state, true)
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !data) return null
  if (!timePeriodRanges.start || !timePeriodRanges.end) return null

  const totals = {current, previous}

  const values = calculateTrafficData(
    today,
    timePeriod,
    data,
    true,
    [dataKey],
    [dataKey],
    timePeriodRanges,
    totals
  ).filter(value => !!value[`${dataKey}Sum`])

  return sortBy(values, value => {
    return sortDirection === 'asc' ? value[sortKey] : -value[sortKey]
  })
}

export function getTrafficData(state, timePeriodRanges) {
  const {today} = state
  const {
    data,
    sortKey,
    sortDirection,
    totalCurrent: current,
    totalPrevious: previous
  } = state.insights.traffic
  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !data) return null
  if (!timePeriodRanges.start || !timePeriodRanges.end) return null

  const totals = {current, previous}
  const values = calculateTrafficData(
    today,
    timePeriod,
    data,
    false,
    ['traffic'],
    [],
    timePeriodRanges,
    totals
  )
  return sortBy(values, value => {
    return sortDirection === 'asc' ? value[sortKey] : -value[sortKey]
  })
}

export function getIncommingTrafficData(state) {
  const {
    metrics,
    total
  } = state.insights.incommings
  return _.map(metrics, (value, metric) => {
    return {
      metric,
      total: total?.[metric] || 0,
      ...value
    }
  })
}

export function getPaidTrafficData(state, timePeriodRanges, dataKey) {
  const {today} = state
  const {
    data,
    totalCurrent: current,
    totalPrevious: previous
  } = state.insights.traffic
  const {sortKey, sortDirection} = getSorting(state, false)

  const timePeriod = getCurrentTimePeriod(state)
  if (!timePeriod || !data) return null
  if (!timePeriodRanges.start || !timePeriodRanges.end) return null

  const totals = {current, previous}

  const values = calculateTrafficData(
    today,
    timePeriod,
    data,
    true,
    [dataKey],
    [dataKey],
    timePeriodRanges,
    totals
  ).filter(value => !!value[`${dataKey}Sum`])

  return sortBy(values, value => {
    return sortDirection === 'asc' ? value[sortKey] : -value[sortKey]
  })
}

export function getLandingsData(state) {
  const {worstData, bestData, valuableData, frequentData} =
    state.insights.landings
  if (!worstData || !bestData || !valuableData || !frequentData) return null

  const processData = data => {
    return Object.keys(data).map(url => {
      const {cr, br, total, traffic, vpv} = data[url]
      return {
        label: url,
        total,
        traffic,
        cr,
        br,
        vpv
      }
    })
  }

  return [
    {
      title: 'Most Valuable',
      data: processData(valuableData)
    },
    {
      title: 'Most Frequent',
      data: processData(frequentData)
    },
    {
      title: 'Worst Performing',
      data: processData(worstData)
    },
    {
      title: 'Best Performing',
      data: processData(bestData)
    }
  ]
}

export function getLandingsDataFetching(state) {
  const {
    isWorstDataFetching,
    isBestDataFetching,
    isValuableDataFetching,
    isFrequentDataFetching
  } = state.insights.landings

  return (
    isWorstDataFetching ||
    isBestDataFetching ||
    isValuableDataFetching ||
    isFrequentDataFetching
  )
}

export function getSelectedChannel(state) {
  const {selectedChannel} = state.insights.landings
  if (!selectedChannel) return null
  const {channelGroupId} = selectedChannel
  if (!channelGroupId) return null

  return selectedChannel
}

const conversionSectionsData = {
  traffic: {
    label: 'Total visits',
    metricLabel: 'Bounce Rate',
    value: ({registrationStarted, traffic}) =>
      1 - registrationStarted / traffic,
    trend: (v1, v2) => v1 < v2
  },
  registrationStarted: {
    label: 'Registration started',
    metricLabel: 'Failed Registration',
    value: ({registrationFinished, registrationStarted}) =>
      1 - registrationFinished / registrationStarted,
    trend: (v1, v2) => v1 < v2
  },
  registrationFinished: {
    label: 'Registration finished',
    metricLabel: 'Failed Deposit',
    value: ({firstTimeDeposit, registrationFinished}) =>
      1 - firstTimeDeposit / registrationFinished,
    trend: (v1, v2) => v1 < v2
  },
  firstTimeDeposit: {
    label: 'First time deposit',
    metricLabel: 'Failed Bet',
    value: ({betPlaced, firstTimeDeposit}) => 1 - betPlaced / firstTimeDeposit,
    trend: (v1, v2) => v1 < v2
  },
  betPlaced: {
    label: 'Bet placed',
    metricLabel: 'Conversion Rate',
    value: ({betPlaced, traffic}) => betPlaced / traffic,
    trend: (v1, v2) => v1 > v2
  }
}

function hasConversionValues(values, channel) {
  if (!values) return false

  return Object.keys(values).reduce((acc, key) => {
    acc += values[key][channel] || 0
    return acc
  }, 0)
}

function getConversionDataForChannel(channel, conversion, traffic) {
  const name = getChannelFullName(channel)
  if (!hasConversionValues(conversion, name)) return null

  const conversionData = Object.keys(conversion).reduce(
    (acc, key) => {
      if (key === 'bounced') return acc

      return {
        ...acc,
        [key]: conversion[key][channel]
      }
    },
    {traffic}
  )

  return Object.keys(conversionData).reduce((acc, sectionId, index, arr) => {
    const sectionData = conversionSectionsData[sectionId]
    if (!sectionData) {
      return {}
    }
    const value = conversionData[sectionId]

    const calculatedValue = value ? sectionData.value(conversionData) : null
    return {
      ...acc,
      [sectionId]: {
        value,
        calculatedValue
      }
    }
  }, {})
}

function calculateTrend(channelData, averageData, key) {
  const trendFunc = conversionSectionsData[key].trend
  if (!channelData[key]) {
    return null
  }
  return trendFunc(
    channelData[key].calculatedValue,
    averageData[key].calculatedValue
  )
}

export function getConversionData(state) {
  const {
    selectedChannels,
    conversionAverage,
    conversionForChannel,
    channelTraffic,
    totalTraffic
  } = state.insights.funnels
  if (!conversionAverage || !totalTraffic || !channelTraffic) return null

  const averageData = getConversionDataForChannel(
    'average',
    conversionAverage,
    totalTraffic
  )
  const channelsData = selectedChannels.reduce(
    (acc, channel) => {
      if (!channel || !conversionForChannel) return acc
      const name = getChannelFullName(channel)

      return {
        ...acc,
        [name]: getConversionDataForChannel(
          name,
          conversionForChannel,
          channelTraffic[name]
        )
      }
    },
    {average: averageData}
  )

  return Object.keys(conversionSectionsData).reduce((acc, key) => {
    const {label, metricLabel} = conversionSectionsData[key]
    const values = Object.keys(channelsData).reduce((acc, channel) => {
      const channelData = channelsData[channel]
      if (channelData && channelData[key]) {
        channelData[key].trend = calculateTrend(channelData, averageData, key)
      }
      return {
        ...acc,
        [channel]: channelData ? channelData[key] : null
      }
    }, {})

    return {
      ...acc,
      [key]: {
        label,
        metricLabel,
        ...values
      }
    }
  }, {})
}

export function getConversionFetching(state) {
  const {
    conversionFetching,
    conversionForChannelFetching,
    channelTrafficFetching,
    totalTrafficFetching
  } = state.insights.funnels

  return (
    conversionFetching ||
    conversionForChannelFetching ||
    channelTrafficFetching ||
    totalTrafficFetching
  )
}

export function getAvailableChannels(state) {
  const {channelTraffic} = state.insights.funnels
  if (!channelTraffic) return null
  const items = Object.keys(channelTraffic)

  return {
    groups: items.filter(item => !item.includes('/')),
    channels: items.filter(item => item.includes('/'))
  }
}

const ATTRIBUTION_MODELS = {
  lastClick: {
    model: 'Last Click model',
    description:
      'The Last Click model attributes 100% of the conversion value to the last visit before the purchase'
  },
  linear: {
    model: 'Linear model',
    description:
      'The Linear model gives equal credit to each visit that the customer made before the conversion.'
  },
  divvit: {
    model: 'Data-Driven model',
    description:
      'Data-Driven model uses a machine learning algorithm that customizes attribution based on customer behaviour throughout the customer journey before the purchase'
  },
  nonDirectLastClick: {
    model: 'Last Click (Non Direct)'
  },
  nonDirectClicks: {
    model: 'Data Driven (PC)'
  },
  nonDirectTouch: {
    model: 'Data Driven (PV)'
  },
  nonDirectLastTouch: {
    model: 'Last Touch (Non Direct)'
  }
}

const METRICS = {
  cost_per_order: {
    'cost:perOrderLast-rf': {
      label: 'Cost/Conversion (Last Click)',
      model: 'lastClick'
    },
    'cost:perOrder-rf': {
      label: 'Cost/Conversion (Linear)',
      model: 'linear'
    },
    'cost:perOrder-divvit-rf': {
      label: 'Cost/Conversion (Data-Driven)',
      model: 'divvit'
    },
    'cost:perOrderLast-ftd': {
      label: 'Cost/Conversion (Last Click)',
      model: 'lastClick'
    },
    'cost:perOrder-ftd': {
      label: 'Cost/Conversion (Linear)',
      model: 'linear'
    },
    'cost:perOrder-divvit-ftd': {
      label: 'Cost/Conversion (Data-Driven)',
      model: 'divvit'
    }
  },
  order_count: {
    'order:count-non-direct-touch': {
      label: 'Conversion Count (Data Driven (PV))',
      model: 'nonDirectTouch'
    },
    'order:count-non-direct-clicks': {
      label: 'Conversion Count (Data Driven (PC))',
      model: 'nonDirectClicks'
    },
    'order:count-non-direct-last-touch': {
      label: 'Conversion Count (Last Touch (Non Direct))',
      model: 'nonDirectLastTouch'
    },
    'order:count-non-direct-last-click': {
      label: 'Conversion Count (Last Click (Non Direct))',
      model: 'nonDirectLastClick'
    }
  },
  order_amount: {
    'order:total-last-click': {
      label: 'Revenue (Last Click)',
      model: 'lastClick'
    },
    'order:total': {
      label: 'Revenue (Linear)',
      model: 'linear'
    },
    'order:total-divvit': {
      label: 'Revenue (Data-Driven)',
      model: 'divvit'
    }
  },
  order_aggr: {
    // 'order:count-every-session': {
    //   label: 'Conversion Count (Touched)',
    //   model: 'touched'
    // },
    'order:count-non-direct-touch': {
      label: 'Conversion Count (Data Driven (PV))',
      model: 'nonDirectTouch'
    },
    'order:count-non-direct-clicks': {
      label: 'Conversion Count (Data Driven (PC))',
      model: 'nonDirectClicks'
    },
    'order:count-non-direct-last-touch': {
      label: 'Conversion Count (Last Touch (Non Direct))',
      model: 'nonDirectLastTouch'
    },
    'order:count-non-direct-last-click': {
      label: 'Conversion Count (Last Click (Non Direct))',
      model: 'nonDirectLastClick'
    }
    // 'order:count-divvit': {
    //   label: 'Conversion Count (Data-Driven)',
    //   model: 'divvit'
    // },
    // 'order:count-last-click': {
    //   label: 'Conversion Count (Last Click)',
    //   model: 'lastClick'
    // }
  }
}

export function getSorting(state, isOrders) {
  const {sortKey, sortDirection} = isOrders
    ? state.insights.orders
    : state.insights.traffic

  if (sortKey === 'trafficSum' || sortKey === 'orderCountSum') {
    return {
      sortKey: isOrders ? 'costPerOrderAverage' : 'costPerVisitAverage',
      sortDirection
    }
  }

  return {sortKey, sortDirection}
}

export function getMetrics(metric) {
  if (!metric) return {}
  const selectedMetrics = METRICS[metric]
  return Object.keys(selectedMetrics).reduce((acc, metric) => {
    const item = selectedMetrics[metric]
    const attributionModel = item.model
    acc[metric] = {
      ...item,
      ...ATTRIBUTION_MODELS[attributionModel]
    }
    return acc
  }, {})
}



// WEBPACK FOOTER //
// ./client/redux/modules/insights/selectors.js